import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import AssistantDropdown from './AssistantDropdown';
import { sendPhenixBatch } from '../services/api';
import './SendBatchModal.css';

const SendBatchModal = ({ isOpen, onClose }) => {
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const fileInputRef = useRef(null);


  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      setError('Please select a valid Excel file (.xlsx or .xls)');
      setFile(null);
      setPreviewData([]);
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess('');
    parseExcelFile(selectedFile);
  };

  const parseExcelFile = (excelFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setError('Excel file must have at least a header row and one data row');
          setPreviewData([]);
          return;
        }

        const headers = jsonData[0];
        const rows = jsonData.slice(1);
        
        console.log('Total rows from Excel:', rows.length);
        
        // Map Excel columns to JSON fields
        const filteredRows = rows.filter(row => {
          // Filter out completely empty rows
          return row.some(cell => cell !== null && cell !== undefined && cell !== '');
        });
        
        console.log('Rows after filtering empty ones:', filteredRows.length);
        
        const mappedData = filteredRows.map((row, index) => {
            const rowData = {};
            headers.forEach((header, colIndex) => {
              const value = row[colIndex];
              
              // Map Excel columns to JSON fields
              if (header === 'Clients') rowData.name = value;
              else if (header === 'Téléphone') rowData.phone = value;
              else if (header === 'Division') rowData.vehicle_division = value;
              else if (header === 'Modèle') rowData.vehicle_model = value;
              else if (header === 'Année') rowData.vehicle_year = value;
              else if (header === 'Notes') rowData.notes = value;
              else if (header === 'Date') rowData.timestamp = value;
              else if (header === 'RV Téléphonique' || header === 'RV') rowData.meeting_date = value;
              else if (header === 'Visite') rowData.visit_date = value;
              else if (header === 'Type') rowData.activix_lead_type = value;
            });

            // Convert Excel date numbers to ISO strings and ensure all fields are strings
            const convertExcelDate = (excelValue) => {
              if (!excelValue || excelValue === '-') return '';

              // Check if it's an Excel date number (Excel dates are numbers > 1)
              if (typeof excelValue === 'number' && excelValue > 1) {
                try {
                  // Excel dates are days since 1900-01-01
                  const excelEpoch = new Date(1900, 0, 1);
                  const date = new Date(excelEpoch.getTime() + (excelValue - 2) * 24 * 60 * 60 * 1000);
                  return date.toISOString();
                } catch (err) {
                  return '';
                }
              }

              // If it's already a string, try to parse it
              if (typeof excelValue === 'string') {
                try {
                  const parsedDate = new Date(excelValue);
                  if (!isNaN(parsedDate.getTime())) {
                    return parsedDate.toISOString();
                  }
                } catch (err) {
                  // Ignore parsing errors
                }
              }

              return '';
            };

            return {
              name: String(rowData.name || ''),
              phone: String(rowData.phone || ''),
              vehicle_division: String(rowData.vehicle_division || ''),
              vehicle_model: String(rowData.vehicle_model || ''),
              vehicle_year: String(rowData.vehicle_year || ''),
              notes: String(rowData.notes || ''),
              timestamp: convertExcelDate(rowData.timestamp),
              meeting_date: convertExcelDate(rowData.meeting_date),
              visit_date: convertExcelDate(rowData.visit_date),
              activix_lead_type: String(rowData.activix_lead_type || '')
            };
          });

        setPreviewData(mappedData.slice(0, 5)); // Show first 5 rows as preview
      } catch (err) {
        setError('Error parsing Excel file. Please ensure it\'s a valid Excel file.');
        setPreviewData([]);
      }
    };
    reader.readAsArrayBuffer(excelFile);
  };

  const handleSubmit = async () => {
    if (!selectedAssistant) {
      setError('Please select an assistant');
      return;
    }

    if (!file) {
      setError('Please select an Excel file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Re-parse the file to get all data
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const headers = jsonData[0];
          const rows = jsonData.slice(1);
          
          console.log('Total rows from Excel (submit):', rows.length);
          
          const filteredRows = rows.filter(row => {
            // Filter out completely empty rows
            return row.some(cell => cell !== null && cell !== undefined && cell !== '');
          });
          
          console.log('Rows after filtering empty ones (submit):', filteredRows.length);
          
          const mappedData = filteredRows.map((row) => {
              const rowData = {};
              headers.forEach((header, colIndex) => {
                const value = row[colIndex];
                
                if (header === 'Clients') rowData.name = value;
                else if (header === 'Téléphone') rowData.phone = value;
                else if (header === 'Division') rowData.vehicle_division = value;
                else if (header === 'Modèle') rowData.vehicle_model = value;
                else if (header === 'Année') rowData.vehicle_year = value;
                else if (header === 'Notes') rowData.notes = value;
                else if (header === 'Date') rowData.timestamp = value;
                else if (header === 'RV Téléphonique' || header === 'RV') rowData.meeting_date = value;
                else if (header === 'Visite') rowData.visit_date = value;
                else if (header === 'Type') rowData.activix_lead_type = value;
              });

              // Convert Excel date numbers to ISO strings and ensure all fields are strings
              const convertExcelDate = (excelValue) => {
                if (!excelValue || excelValue === '-') return '';

                // Check if it's an Excel date number (Excel dates are numbers > 1)
                if (typeof excelValue === 'number' && excelValue > 1) {
                  try {
                    // Excel dates are days since 1900-01-01
                    const excelEpoch = new Date(1900, 0, 1);
                    const date = new Date(excelEpoch.getTime() + (excelValue - 2) * 24 * 60 * 60 * 1000);
                    return date.toISOString();
                  } catch (err) {
                    return '';
                  }
                }

                // If it's already a string, try to parse it
                if (typeof excelValue === 'string') {
                  try {
                    const parsedDate = new Date(excelValue);
                    if (!isNaN(parsedDate.getTime())) {
                      return parsedDate.toISOString();
                    }
                  } catch (err) {
                    // Ignore parsing errors
                  }
                }

                return '';
              };

              return {
                name: String(rowData.name || ''),
                phone: String(rowData.phone || ''),
                vehicle_division: String(rowData.vehicle_division || ''),
                vehicle_model: String(rowData.vehicle_model || ''),
                vehicle_year: String(rowData.vehicle_year || ''),
                notes: String(rowData.notes || ''),
                timestamp: convertExcelDate(rowData.timestamp),
                meeting_date: convertExcelDate(rowData.meeting_date),
                visit_date: convertExcelDate(rowData.visit_date),
                activix_lead_type: String(rowData.activix_lead_type || '')
              };
            });

                      console.log('Final mapped data:', mappedData);
            console.log('Sample data types:', {
              name: typeof mappedData[0]?.name,
              phone: typeof mappedData[0]?.phone,
              vehicle_year: typeof mappedData[0]?.vehicle_year,
              timestamp: typeof mappedData[0]?.timestamp,
              visit_date: typeof mappedData[0]?.visit_date
            });

          // Send to API
          await sendPhenixBatch(selectedAssistant, mappedData);
          setSuccess(`Successfully sent ${mappedData.length} records to the assistant`);
          
          // Reset form
          setTimeout(() => {
            onClose();
            setFile(null);
            setSelectedAssistant('');
            setPreviewData([]);
            setSuccess('');
            setError('');
          }, 2000);
        } catch (err) {
          setError('Error processing file or sending data. Please try again.');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError('Error sending batch data. Please try again.');
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError('');
      setSuccess('');
      parseExcelFile(droppedFile);
    }
  };

  const resetForm = () => {
    setFile(null);
    setSelectedAssistant('');
    setPreviewData([]);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content send-batch-modal">
        <h2>Send Batch</h2>
        
        <div className="form-group">
          <label>Select Assistant:</label>
          <AssistantDropdown
            value={selectedAssistant}
            onChange={setSelectedAssistant}
            placeholder="Choose an assistant..."
          />
        </div>

        <div className="form-group">
          <label>Upload Excel File:</label>
          <div
            className="file-upload-area"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            {file ? (
              <div className="file-selected">
                <span>✓ {file.name}</span>
                <button 
                  type="button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="remove-file-btn"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="file-upload-text">
                <span>📁 Click to browse or drag & drop Excel file</span>
                <small>Supports .xlsx and .xls files</small>
              </div>
            )}
          </div>
        </div>

        {previewData.length > 0 && (
          <div className="form-group">
            <label>Preview (first 5 rows):</label>
            <div className="preview-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Vehicle</th>
                    <th>Type</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.name}</td>
                      <td>{row.phone}</td>
                      <td>{row.vehicle_year} {row.vehicle_model}</td>
                      <td>{row.activix_lead_type}</td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="modal-buttons">
          <button 
            type="button" 
            onClick={onClose}
            disabled={loading}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selectedAssistant || !file || loading}
            className="submit-btn"
          >
            {loading ? 'Sending...' : 'Send Batch'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendBatchModal;